import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  BookOpen, Users, UserCheck, Plus, Trash2, Edit3, ArrowUpRight,
  ArrowDownRight, Search, X, ChevronLeft, Phone, MessageSquare,
  Calendar, IndianRupee, FileText, TrendingUp, TrendingDown, Download,
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../utils/api";
import { CSVLink } from "react-csv";

/* ── constants ─────────────────────────────────────────────────────────────── */
const CUSTOMER_TYPES = [
  { key: "due",     label: "Due / Udhaar",      isDebit: true,  color: "rose",   icon: ArrowUpRight },
  { key: "payment", label: "Payment Received",   isDebit: false, color: "emerald",icon: ArrowDownRight },
];
const EMPLOYEE_TYPES = [
  { key: "salary",    label: "Salary Due",    isDebit: true,  color: "amber",  icon: ArrowUpRight },
  { key: "bonus",     label: "Bonus",         isDebit: true,  color: "purple", icon: ArrowUpRight },
  { key: "advance",   label: "Advance Paid",  isDebit: false, color: "blue",   icon: ArrowDownRight },
  { key: "payment",   label: "Payment Made",  isDebit: false, color: "emerald",icon: ArrowDownRight },
  { key: "deduction", label: "Deduction",     isDebit: false, color: "slate",  icon: ArrowDownRight },
];

const VENDOR_TYPES = [
  { key: "purchase", label: "Purchase (Credit)", isDebit: true,  color: "rose",   icon: ArrowUpRight },
  { key: "payment",  label: "Payment Made",      isDebit: false, color: "emerald",icon: ArrowDownRight },
];

const AUTO_EXPENSE_ENTRY_TYPES = ["salary", "bonus", "advance"];

// lookup: build per entityType
const TYPE_MAP = {
  customer: Object.fromEntries(CUSTOMER_TYPES.map(t => [t.key, t])),
  employee: Object.fromEntries(EMPLOYEE_TYPES.map(t => [t.key, t])),
  vendor:   Object.fromEntries(VENDOR_TYPES.map(t => [t.key, t])),
};

const fmt  = n => Math.abs(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const fmtD = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const EMPTY_FORM = {
  entityName: "", entityMobile: "", entryType: "", amount: "", note: "", date: "", autoExpense: false,
};

/* ── Balance pill ───────────────────────────────────────────────────────────── */
const BalancePill = ({ balance, entityType, size = "sm" }) => {
  const isZero = balance === 0;
  const isOwe  = balance > 0;
  const label  = entityType === "customer"
    ? (isZero ? "Settled" : isOwe ? `Due ₹${fmt(balance)}` : `Advance ₹${fmt(balance)}`)
    : entityType === "vendor"
      ? (isZero ? "Settled" : isOwe ? `You Owe ₹${fmt(balance)}` : `Overpaid ₹${fmt(balance)}`)
      : (isZero ? "Settled" : isOwe ? `Payable ₹${fmt(balance)}` : `Overpaid ₹${fmt(balance)}`);
  const cls = isZero
    ? "bg-gray-100 text-gray-500"
    : isOwe
      ? "bg-rose-50 text-rose-600 border border-rose-200"
      : "bg-emerald-50 text-emerald-600 border border-emerald-200";
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full font-bold whitespace-nowrap ${size === "lg" ? "px-3 py-1 text-sm" : "px-2 py-0.5 text-xs"} ${cls}`}>
      {!isZero && (isOwe ? <TrendingUp size={10}/> : <TrendingDown size={10}/>)}
      {label}
    </span>
  );
};

/* ── Entry type badge ───────────────────────────────────────────────────────── */
const TypeBadge = ({ entryType, entityType, autoLinked }) => {
  const meta = TYPE_MAP[entityType]?.[entryType];
  if (!meta) return <span className="text-xs text-gray-400">{entryType}</span>;
  const colors = {
    rose:    "bg-rose-50 text-rose-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber:   "bg-amber-50 text-amber-700",
    purple:  "bg-purple-50 text-purple-600",
    blue:    "bg-blue-50 text-blue-600",
    slate:   "bg-slate-100 text-slate-600",
  };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${colors[meta.color]}`}>
      <Icon size={10}/>{meta.label}
    </span>
  );
};

/* ════════════════════════════════════════════════════════════════════════════ */
const Ledger = () => {
  const [tab, setTab]                 = useState("customer");  // customer | employee | vendor
  const [entities, setEntities]       = useState([]);
  const [selected, setSelected]       = useState(null);  // { entityName, entityMobile, entityType, balance }
  const [entries, setEntries]         = useState([]);
  const [balance, setBalance]         = useState(0);
  const [search, setSearch]           = useState("");
  const [showModal, setShowModal]     = useState(false);
  const [editEntry, setEditEntry]     = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [loading, setLoading]         = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showDetail, setShowDetail]   = useState(false); // mobile: show right panel
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState(null);

  /* ── fetch entities list ── */
  const fetchEntities = useCallback(async (entityType = tab) => {
    setLoading(true);
    try {
      const res = await api.get("/ledger/entities", { params: { entityType } });
      if (res.data.success) setEntities(res.data.data);
    } catch { toast.error("Failed to load ledger"); }
    finally  { setLoading(false); }
  }, [tab]);

  useEffect(() => {
    setSelected(null);
    setEntries([]);
    setShowDetail(false);
    fetchEntities(tab);
  }, [tab, fetchEntities]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── fetch entries for selected entity ── */
  const fetchEntries = useCallback(async (entity) => {
    if (!entity) return;
    setDetailLoading(true);
    try {
      const res = await api.get("/ledger/entries", {
        params: { entityType: entity.entityType, entityName: entity.entityName, entityMobile: entity.entityMobile },
      });
      if (res.data.success) {
        setEntries(res.data.data);
        setBalance(res.data.balance);
      }
    } catch { toast.error("Failed to load entries"); }
    finally  { setDetailLoading(false); }
  }, []);

  const selectEntity = (entity) => {
    setSelected(entity);
    setShowDetail(true);
    fetchEntries(entity);
  };

  /* ── filtered entity list ── */
  const filteredEntities = useMemo(() => {
    if (!search.trim()) return entities;
    const q = search.toLowerCase();
    return entities.filter(e =>
      e.entityName.toLowerCase().includes(q) ||
      (e.entityMobile || "").includes(q)
    );
  }, [entities, search]);

  /* ── summary stats ── */
  const stats = useMemo(() => {
    const owed    = entities.filter(e => e.balance > 0).reduce((s, e) => s + e.balance, 0);
    const advance = entities.filter(e => e.balance < 0).reduce((s, e) => s + Math.abs(e.balance), 0);
    const owing   = entities.filter(e => e.balance > 0).length;
    return { owed, advance, owing, total: entities.length };
  }, [entities]);

  /* ── CSV data ── */
  const csvData = useMemo(() => entries.map(e => ({
    Date: fmtD(e.date),
    Type: TYPE_MAP[e.entityType]?.[e.entryType]?.label || e.entryType,
    Amount: e.amount,
    "Running Balance": e.runningBalance,
    Note: e.note || "",
  })), [entries]);

  /* ── open add modal ── */
  const openAdd = () => {
    setEditEntry(null);
    setForm({
      ...EMPTY_FORM,
      entityName:   selected?.entityName   || "",
      entityMobile: selected?.entityMobile || "",
      date: new Date().toISOString().slice(0, 10),
    });
    setShowModal(true);
  };

  /* ── open edit modal ── */
  const openEdit = (entry) => {
    setEditEntry(entry);
    setForm({
      entityName:   entry.entityName,
      entityMobile: entry.entityMobile || "",
      entryType:    entry.entryType,
      amount:       String(entry.amount),
      note:         entry.note || "",
      date:         entry.date ? entry.date.slice(0, 10) : "",
    });
    setShowModal(true);
  };

  /* ── save entry ── */
  const saveEntry = async () => {
    if (!form.entityName.trim()) return toast.error("Name is required");
    if (!form.entryType)         return toast.error("Select entry type");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a valid amount");

    setSaving(true);
    try {
      const payload = {
        entityType:   tab,
        entityName:   form.entityName.trim(),
        entityMobile: form.entityMobile.trim(),
        entryType:    form.entryType,
        amount:       Number(form.amount),
        note:         form.note,
        date:         form.date || new Date().toISOString().slice(0, 10),
        ...(tab === "employee" && AUTO_EXPENSE_ENTRY_TYPES.includes(form.entryType) && { autoExpense: form.autoExpense }),
      };

      if (editEntry) {
        await api.put(`/ledger/entry/${editEntry._id}`, payload);
        toast.success("Entry updated");
      } else {
        const res = await api.post("/ledger/entry", payload);
        if (res.data?.autoExpense) {
          toast.success("Entry added & expense auto-created in Staff & Salaries");
        } else {
          toast.success("Entry added");
        }
      }

      setShowModal(false);
      await fetchEntities(tab);

      // Refresh detail if same entity or new entity selected
      const newEntity = { entityType: tab, entityName: payload.entityName, entityMobile: payload.entityMobile };
      if (!selected || (selected.entityName === payload.entityName && selected.entityMobile === payload.entityMobile)) {
        setSelected(newEntity);
        setShowDetail(true);
        fetchEntries(newEntity);
      } else {
        fetchEntries(selected);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save entry");
    } finally {
      setSaving(false);
    }
  };

  /* ── delete entry ── */
  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    setDeleting(id);
    try {
      await api.delete(`/ledger/entry/${id}`);
      toast.success("Entry deleted");
      await fetchEntities(tab);
      if (selected) fetchEntries(selected);
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDeleting(null);
    }
  };

  /* ── WhatsApp reminder ── */
  const sendWhatsApp = () => {
    if (!selected?.entityMobile) return toast.error("No mobile number on record");
    const msg = `Hi ${selected.entityName}, your outstanding balance is ₹${fmt(balance)}. Please settle at your earliest convenience. Thank you!`;
    window.open(`https://wa.me/91${selected.entityMobile}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const types = tab === "customer" ? CUSTOMER_TYPES : tab === "vendor" ? VENDOR_TYPES : EMPLOYEE_TYPES;

  /* ════════ RENDER ════════ */
  return (
    <div className="min-h-screen bg-slate-50 pt-16 pb-10">

      {/* ── Page header ── */}
      <div className="px-4 sm:px-6 lg:px-8 py-5 max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 bg-indigo-100 rounded-xl">
            <BookOpen size={22} className="text-indigo-600"/>
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Ledger Book</h1>
            <p className="text-sm text-gray-400">Track dues, payments and vendor credit for customers, employees & vendors</p>
          </div>
          <button onClick={openAdd}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition shadow-sm">
            <Plus size={16}/> Add Entry
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mb-4">
        <div className="flex gap-1 bg-white border border-gray-200 rounded-2xl p-1 w-fit shadow-sm">
          {[
            { key: "customer", label: "Customers", icon: Users },
            { key: "employee", label: "Employees", icon: UserCheck },
            { key: "vendor",   label: "Vendors",   icon: BookOpen },
          ].map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold transition-all ${tab === key ? "bg-indigo-600 text-white shadow" : "text-gray-500 hover:bg-gray-100"}`}>
              <Icon size={15}/>{label}
            </button>
          ))}
        </div>
      </div>

      {/* ── KPI strip ── */}
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mb-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: tab === "customer" ? "Total Dues" : tab === "vendor" ? "Total Payable" : "Total Payable", value: `₹${fmt(stats.owed)}`,   color: "text-rose-600",    bg: "bg-rose-50" },
          { label: tab === "customer" ? "Advance Paid" : tab === "vendor" ? "Overpaid" : "Overpaid",         value: `₹${fmt(stats.advance)}`, color: "text-emerald-600", bg: "bg-emerald-50" },
          { label: tab === "customer" ? "Customers Owing" : tab === "vendor" ? "Vendors Owed" : "Employees Owed", value: stats.owing, color: "text-amber-600", bg: "bg-amber-50" },
          { label: tab === "customer" ? "Total Customers" : tab === "vendor" ? "Total Vendors" : "Total Employees", value: stats.total, color: "text-indigo-600", bg: "bg-indigo-50" },
        ].map(c => (
          <div key={c.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 flex flex-col gap-1">
            <span className="text-xs text-gray-400 font-medium">{c.label}</span>
            <span className={`text-xl font-extrabold ${c.color}`}>{c.value}</span>
          </div>
        ))}
      </div>

      {/* ── Split panel ── */}
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex" style={{ minHeight: "520px" }}>

          {/* ── LEFT: entity list ── */}
          <div className={`flex flex-col border-r border-gray-100 ${showDetail ? "hidden lg:flex" : "flex"} w-full lg:w-80 xl:w-96 flex-shrink-0`}>
            {/* search */}
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                <input type="text" placeholder={`Search ${tab === "vendor" ? "vendors" : tab + "s"}…`} value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400"/>
              </div>
            </div>

            {/* list */}
            <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"/>
                </div>
              ) : filteredEntities.length === 0 ? (
                <div className="py-16 text-center text-gray-300">
                  <BookOpen size={36} className="mx-auto mb-3 opacity-40"/>
                  <p className="text-sm">No {tab === "vendor" ? "vendors" : tab + "s"} yet</p>
                  <button onClick={openAdd} className="mt-3 text-indigo-500 text-sm font-semibold hover:underline">+ Add first entry</button>
                </div>
              ) : filteredEntities.map((e, i) => {
                const isSelected = selected?.entityName === e.entityName && selected?.entityMobile === e.entityMobile;
                return (
                  <button key={i} onClick={() => selectEntity(e)}
                    className={`w-full text-left px-4 py-3.5 hover:bg-slate-50 transition flex items-center gap-3 ${isSelected ? "bg-indigo-50 border-l-2 border-indigo-500" : ""}`}>
                    {/* avatar */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0 ${isSelected ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-500"}`}>
                      {e.entityName.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">{e.entityName}</p>
                      {e.entityMobile && <p className="text-xs text-gray-400 mt-0.5">{e.entityMobile}</p>}
                      <p className="text-xs text-gray-300 mt-0.5">{e.totalEntries} entries · {fmtD(e.lastDate)}</p>
                    </div>
                    <BalancePill balance={e.balance} entityType={e.entityType}/>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── RIGHT: entry detail ── */}
          <div className={`flex-1 flex flex-col min-w-0 ${!showDetail ? "hidden lg:flex" : "flex"}`}>
            {!selected ? (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-200 gap-3">
                <BookOpen size={48} className="opacity-30"/>
                <p className="text-sm text-gray-400">Select a {tab === "vendor" ? "vendor" : tab} to view ledger</p>
                <button onClick={openAdd} className="mt-2 flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 text-sm font-semibold rounded-xl hover:bg-indigo-100 transition">
                  <Plus size={14}/>Add New Entry
                </button>
              </div>
            ) : (
              <>
                {/* Detail header */}
                <div className="px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3">
                  <button onClick={() => { setShowDetail(false); setSelected(null); }}
                    className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 self-start">
                    <ChevronLeft size={18}/>
                  </button>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-11 h-11 rounded-full bg-indigo-600 text-white flex items-center justify-center text-lg font-black flex-shrink-0">
                      {selected.entityName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h2 className="font-extrabold text-gray-900 text-base">{selected.entityName}</h2>
                      {selected.entityMobile && (
                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                          <Phone size={10}/>{selected.entityMobile}
                        </p>
                      )}
                    </div>
                    <div className="ml-2">
                      <BalancePill balance={balance} entityType={tab} size="lg"/>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {tab === "customer" && selected.entityMobile && (
                      <button onClick={sendWhatsApp} title="Send WhatsApp reminder"
                        className="flex items-center gap-1.5 px-3 py-2 bg-green-50 text-green-600 rounded-xl text-xs font-bold hover:bg-green-100 transition border border-green-200">
                        <MessageSquare size={13}/>Remind
                      </button>
                    )}
                    {entries.length > 0 && (
                      <CSVLink data={csvData} filename={`ledger-${selected.entityName}.csv`}
                        className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 text-gray-600 rounded-xl text-xs font-bold hover:bg-gray-100 transition border border-gray-200">
                        <Download size={13}/>Export
                      </CSVLink>
                    )}
                    <button onClick={openAdd}
                      className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 transition shadow-sm">
                      <Plus size={13}/>Add Entry
                    </button>
                  </div>
                </div>

                {/* Entries timeline */}
                <div className="flex-1 overflow-y-auto px-5 py-4">
                  {detailLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"/>
                    </div>
                  ) : entries.length === 0 ? (
                    <div className="py-16 text-center text-gray-300">
                      <FileText size={36} className="mx-auto mb-3 opacity-40"/>
                      <p className="text-sm">No entries yet</p>
                    </div>
                  ) : (
                    <div className="relative">
                      {/* vertical timeline line */}
                      <div className="absolute left-5 top-0 bottom-0 w-px bg-gray-100"/>
                      <div className="space-y-0">
                        {entries.map((entry, idx) => {
                          const meta   = TYPE_MAP[tab]?.[entry.entryType];
                          const isDebt = meta?.isDebit ?? false;
                          const Icon   = meta?.icon || ArrowUpRight;
                          return (
                            <div key={entry._id} className="relative flex gap-4 pb-4">
                              {/* dot */}
                              <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border-2 border-white shadow-sm ${isDebt ? "bg-rose-100" : "bg-emerald-100"}`}>
                                <Icon size={16} className={isDebt ? "text-rose-600" : "text-emerald-600"}/>
                              </div>
                              {/* card */}
                              <div className="flex-1 bg-gray-50 rounded-xl p-3 hover:bg-gray-100/70 transition group">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                      <TypeBadge entryType={entry.entryType} entityType={tab}/>
                                      <span className={`text-sm font-extrabold ${isDebt ? "text-rose-600" : "text-emerald-600"}`}>
                                        {isDebt ? "+" : "−"} ₹{fmt(entry.amount)}
                                      </span>
                                    </div>
                                    {entry.note && <p className="text-xs text-gray-500 mb-1">{entry.note}</p>}
                                    <div className="flex items-center gap-3 text-xs text-gray-400">
                                      <span className="flex items-center gap-1"><Calendar size={10}/>{fmtD(entry.date)}</span>
                                      <span className="flex items-center gap-1">
                                        <IndianRupee size={10}/>
                                        Balance: <span className={`font-bold ml-0.5 ${entry.runningBalance > 0 ? "text-rose-500" : entry.runningBalance < 0 ? "text-emerald-500" : "text-gray-400"}`}>
                                          ₹{fmt(entry.runningBalance)}
                                        </span>
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition flex-shrink-0">
                                    <button onClick={() => openEdit(entry)}
                                      className="p-1.5 rounded-lg hover:bg-white text-gray-400 hover:text-indigo-600 transition">
                                      <Edit3 size={13}/>
                                    </button>
                                    <button onClick={() => deleteEntry(entry._id)} disabled={deleting === entry._id}
                                      className="p-1.5 rounded-lg hover:bg-white text-gray-400 hover:text-rose-600 transition">
                                      {deleting === entry._id
                                        ? <div className="w-3 h-3 border border-rose-400 border-t-transparent rounded-full animate-spin"/>
                                        : <Trash2 size={13}/>}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ════════ ADD / EDIT MODAL ════════ */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
            {/* modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-extrabold text-gray-900 flex items-center gap-2">
                <BookOpen size={16} className="text-indigo-600"/>
                {editEntry ? "Edit Entry" : "Add Ledger Entry"}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X size={16}/></button>
            </div>

            <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">

              {/* entity type selector (only when no entity selected) */}
              {!editEntry && (
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Entry For</label>
                  <div className="flex gap-2">
                    {[
                      { key: "customer", label: "Customer", icon: Users },
                      { key: "employee", label: "Employee", icon: UserCheck },
                      { key: "vendor",   label: "Vendor",   icon: BookOpen },
                    ].map(({ key, label, icon: Icon }) => (
                      <button key={key} onClick={() => setTab(key)}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border text-sm font-bold transition ${tab === key ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-200 text-gray-500 hover:border-indigo-300"}`}>
                        <Icon size={14}/>{label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* name */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  {tab === "customer" ? "Customer" : tab === "vendor" ? "Vendor" : "Employee"} Name *
                </label>
                <input type="text" placeholder="Full name" value={form.entityName}
                  onChange={e => setForm(f => ({ ...f, entityName: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"/>
              </div>

              {/* mobile */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Mobile (optional)</label>
                <input type="tel" placeholder="10-digit mobile" value={form.entityMobile}
                  onChange={e => setForm(f => ({ ...f, entityMobile: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"/>
              </div>

              {/* entry type */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Entry Type *</label>
                <div className="grid grid-cols-2 gap-2">
                  {types.map(t => (
                    <button key={t.key} onClick={() => setForm(f => ({ ...f, entryType: t.key }))}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-semibold transition text-left ${form.entryType === t.key ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-200 text-gray-600 hover:border-indigo-300"}`}>
                      <t.icon size={14}/>{t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* amount */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Amount (₹) *</label>
                <div className="relative">
                  <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                  <input type="number" min="0" placeholder="0.00" value={form.amount}
                    onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    className="w-full pl-8 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"/>
                </div>
              </div>

              {/* date */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Date</label>
                <input type="date" value={form.date}
                  onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  max={new Date().toISOString().slice(0, 10)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"/>
              </div>

              {/* note */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Note (optional)</label>
                <textarea rows={2} placeholder="e.g. Bill #101, partial payment…" value={form.note}
                  onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"/>
              </div>

              {/* Auto-create expense — shown for employee salary/bonus/advance */}
              {!editEntry && tab === "employee" && AUTO_EXPENSE_ENTRY_TYPES.includes(form.entryType) && (
                <div className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-100 rounded-xl">
                  <input type="checkbox" id="autoExpense" checked={form.autoExpense}
                    onChange={e => setForm(f => ({ ...f, autoExpense: e.target.checked }))}
                    className="w-4 h-4 accent-indigo-600 cursor-pointer"/>
                  <label htmlFor="autoExpense" className="text-sm text-indigo-700 font-medium cursor-pointer select-none">
                    Auto-add to Expenses <span className="text-xs font-normal text-indigo-500">(creates a "Staff & Salaries" expense entry)</span>
                  </label>
                </div>
              )}
            </div>

            {/* modal footer */}
            <div className="px-5 py-4 border-t border-gray-100 flex gap-3">
              <button onClick={() => setShowModal(false)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-50 transition">
                Cancel
              </button>
              <button onClick={saveEntry} disabled={saving}
                className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 transition disabled:opacity-60 flex items-center justify-center gap-2">
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/> : null}
                {editEntry ? "Update" : "Save Entry"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ledger;
